import { useIntl } from 'react-intl';
import { UncontrolledTooltip } from 'reactstrap';
import { ArrowLeft, Maximize2, Settings, Star, X } from 'lucide-react';
import history from 'src/history';
import { useEffect, useState } from 'react';
import http from 'src/services/httpService';
import ReactGA from 'react-ga4';
import { useSelector } from 'react-redux';
import RenomearFiltro from 'src/paginas/filtrofavorito/Renomear';
import ContentManager from 'src/componentes/content-manager';
import { useSnackbar } from 'notistack';
import CriticalConfirm from 'src/componentes/message-box/critical-confirm';
import { capitalizeFirstLetter } from 'src/utils/string';
import errorHandler from 'src/utils/error-handler';

const CHAVE_TELA_CHEIA = 'content-tela-cheia';

const lerTelaCheia = () => {
  try {
    return sessionStorage.getItem(CHAVE_TELA_CHEIA) === '1';
  } catch {
    return false;
  }
};

const gravarTelaCheia = (valor) => {
  try {
    if (valor) sessionStorage.setItem(CHAVE_TELA_CHEIA, '1');
    else sessionStorage.removeItem(CHAVE_TELA_CHEIA);
  } catch {
  }
};

const Content = ({
  titulo,
  isEllipsis = false,
  children,
  iconSettings = false,
  titleSettings = null,
  urlSettings = '',
  endPointPermissionSettings = null,
  actionsTop = null,
  iconFullScreen = true,
  handleFullScreen = null,
  showBackButton = true,
  handleClickSettings = null,
  handleClickBack = null,
  iconFavorite = false,
  filtroFavorito = {},
  fullHeight = false,
  ...rest
}) => {
  const id = 'PAGE-TITLE';
  const idFullscreen = 'content-tela-cheia';
  const intl = useIntl();
  const user = useSelector((state) => state.user);
  const [hasAccessSettings, setHasAccessSettings] = useState(
    endPointPermissionSettings == null
  );
  const [telaCheia, setTelaCheia] = useState(
    () => iconFullScreen && !handleFullScreen && lerTelaCheia()
  );
  const { enqueueSnackbar } = useSnackbar();

  const [ehFavorito, setEhFavorito] = useState(
    filtroFavorito?.ehFavorito ?? false
  );

  useEffect(() => {
    setEhFavorito(filtroFavorito?.ehFavorito ?? false);
  }, [filtroFavorito?.ehFavorito, filtroFavorito?.id]);

  const atualizarFavorito = () => {
    setEhFavorito((prev) => !prev);
  };

  const favoritarFiltro = () => {
    const url = `/FiltroColaborador/${filtroFavorito.tipoFiltro}/AtualizarNome`;
    ContentManager.addContent(
      <RenomearFiltro
        id={filtroFavorito.id}
        nome={''}
        url={url}
        isNew={true}
        updateCallback={atualizarFavorito}
      />
    );
  };

  const desfavoritar = async () => {
    try {
      const url = `/FiltroColaborador/${filtroFavorito.tipoFiltro}/DesfavoritarFavorito`;
      const response = await http.post(url, {
        filtroFavoritoId: filtroFavorito.id,
      });

      if (response) {
        atualizarFavorito();

        enqueueSnackbar(
          intl.formatMessage({ id: 'label.registroSalvoComSucesso' }),
          {
            variant: 'success',
            autoHideDuration: 3000,
          }
        );
      }
    } catch (err) {
      errorHandler(err);
    }
  };

  const desfavoritarFiltro = () => {
    ContentManager.addContent(
      <CriticalConfirm
        title={`${intl.formatMessage({
          id: 'excluir',
        })} ${intl.formatMessage({
          id: 'favorito',
        })}`}
        message={capitalizeFirstLetter(
          intl.formatMessage({ id: 'temCertezaExcluir' })
        )}
        handleConfirm={() => desfavoritar()}
        labelConfirm={intl.formatMessage({
          id: 'label.confirmarExclusao',
        })}
      />
    );
  };

  const onClickSettings = () => {
    const textoGA = titleSettings ?? `Configurações ${titulo}`;
    ReactGA.event(`${textoGA}`, {
      category: 'configuracao',
      action: textoGA,
      label: user?.nome,
    });

    if (urlSettings) history.push(urlSettings);
    else if (handleClickSettings) handleClickSettings();
  };

  const onClickBack = () => {
    if (handleClickBack) handleClickBack();
    else history.goBack();
  };

  const onClickFullScreen = () => {
    if (handleFullScreen) handleFullScreen();
    else {
      setTelaCheia((atual) => {
        const proximo = !atual;
        gravarTelaCheia(proximo);
        return proximo;
      });
    }
  };

  const [canGoBack, setCanGoBack] = useState(history.length > 1);

  useEffect(() => {
    if (iconSettings && endPointPermissionSettings) {
      http.post(endPointPermissionSettings).then((response) => {
        setHasAccessSettings(response.data);
      });
    }
  }, [endPointPermissionSettings]);

  useEffect(() => {
    const unsubscribe = history.listen(({ action }) => {
      if (action === 'POP') {
        setCanGoBack(history.length > 1);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!telaCheia) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setTelaCheia(false);
        gravarTelaCheia(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [telaCheia]);

  const telaCheiaAtiva = telaCheia && iconFullScreen && !handleFullScreen;

  return (
    <div
      className={`no-style p-3${telaCheiaAtiva ? ' content-tela-cheia' : ''}${
        fullHeight ? ' content-full-height' : ''
      }`}
      {...rest}
    >
      {titulo && (
        <>
          <h3
            className={`pb-3 mb-0 ignoreInPrint ${
              isEllipsis ? 'text-truncate' : ''
            }`}
            style={{ fontSize: 20 }}
          >
            <div className="d-flex justify-content-between">
              <div className="d-flex">
                {showBackButton && canGoBack && (
                  <div
                    className="text-primary cursor-pointer mr-2"
                    style={{ marginTop: '-2px' }}
                  >
                    <ArrowLeft size={22} onClick={onClickBack} />
                  </div>
                )}
                <span id={id}>{titulo}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {iconFavorite && filtroFavorito && filtroFavorito.id != 0 && (
                  <div style={{ marginRight: '8px' }}>
                    <Star
                      key={ehFavorito ? 'star-on' : 'star-off'}
                      className="text-primary"
                      fill={ehFavorito ? 'currentColor' : 'none'}
                      size={19}
                      style={{ fontSize: '24px', cursor: 'pointer' }}
                      onClick={() => {
                        if (!ehFavorito) {
                          favoritarFiltro();
                        } else {
                          desfavoritarFiltro();
                        }
                      }}
                    />
                  </div>
                )}
                {hasAccessSettings && iconSettings && (
                  <div id={'tooltipSettings'} style={{ marginRight: '8px' }}>
                    <Settings
                      className="text-primary"
                      size={19}
                      onClick={onClickSettings}
                      style={{ cursor: 'pointer' }}
                    />
                    {titleSettings && (
                      <UncontrolledTooltip
                        target={'tooltipSettings'}
                        placement="auto"
                      >
                        {titleSettings}
                      </UncontrolledTooltip>
                    )}
                  </div>
                )}
                {iconFullScreen && (
                  <div id="tooltipFullScreen" style={{ marginRight: '8px' }}>
                    {telaCheiaAtiva ? (
                      <X
                        id={idFullscreen}
                        className="text-primary"
                        size={19}
                        style={{ cursor: 'pointer' }}
                        onClick={onClickFullScreen}
                      />
                    ) : (
                      <Maximize2
                        id={idFullscreen}
                        className="text-primary"
                        size={19}
                        style={{ cursor: 'pointer' }}
                        onClick={onClickFullScreen}
                      />
                    )}
                    <UncontrolledTooltip target={idFullscreen}>
                      {intl.formatMessage({ id: 'telaCheia' })}
                    </UncontrolledTooltip>
                  </div>
                )}
                {actionsTop && <>{actionsTop}</>}
              </div>
            </div>
          </h3>
          {isEllipsis && (
            <UncontrolledTooltip target={id} placement="auto">
              {titulo}
            </UncontrolledTooltip>
          )}
        </>
      )}
      {children}
    </div>
  );
};

export default Content;
